import { type BigIntish, type MarketId, MathLib } from '@morpho-org/blue-sdk'
import { toBigInt } from '@morpho-org/dapp-core'

type VaultV2Gate = { address?: string | null } | null

export type VaultV2Gates =
  | {
      receiveAssetsGate?: VaultV2Gate
      receiveSharesGate?: VaultV2Gate
      sendAssetsGate?: VaultV2Gate
      sendSharesGate?: VaultV2Gate
    }
  | null
  | undefined

/**
 * Checks whether a VaultV2 has any active gate set.
 * A vault is considered gated if at least one of its four gates is non-null.
 */
export function isVaultV2Gated(gates: VaultV2Gates): boolean {
  return !!(
    gates?.receiveAssetsGate?.address ||
    gates?.receiveSharesGate?.address ||
    gates?.sendAssetsGate?.address ||
    gates?.sendSharesGate?.address
  )
}

/**
 * Checks whether a VaultV2 has a gate on the exit path (withdraw/redeem).
 * On-chain, `exit` checks `sendSharesGate` on `onBehalf` and `receiveAssetsGate` on `receiver`;
 * the entry gates (`receiveSharesGate`, `sendAssetsGate`) don't apply to withdrawals.
 */
export function isVaultV2ExitGated(gates: VaultV2Gates): boolean {
  return !!(gates?.sendSharesGate?.address || gates?.receiveAssetsGate?.address)
}

/**
 * Calculate the share from a total.
 *
 * @param total - The total of supply/asset.
 * @param share - The share in the total.
 *
 * @returns A bigint representing the share from a total.
 *
 * @example
 * getShareFromTotal(10000, 20) // returns 2
 */
export const getShareFromTotal = (
  total: BigIntish | null | undefined,
  share: BigIntish | null | undefined
) => {
  const totalBigInt = toBigInt(total)
  const shareBigInt = toBigInt(share)
  if (!shareBigInt || !totalBigInt) return 0n
  return MathLib.min(MathLib.wDivDown(shareBigInt, totalBigInt), MathLib.WAD)
}

interface AllocationWithMarket {
  market: { marketId: MarketId }
}

/**
 * Finds the allocation for a specific market in a vault's allocation array.
 * @param allocation - Array of vault allocations
 * @param marketId - Market id to search for
 * @returns Matching allocation or undefined if not found
 * @internal
 */
export const findMarketAllocation = <T extends AllocationWithMarket>(
  allocation: T[],
  marketId: string
): T | undefined => {
  return allocation.find((allocation: T) => allocation.market.marketId === marketId)
}
